const LINK_SELECTOR = 'a[data-linked-product-navigation]';
const MAIN_PRODUCT_SELECTOR = '.shopify-section--main-product';
const LOADING_DELAY_MS = 200;
const prefetchedUrls = new Set();

let activeController;
let activeNavigationId = 0;
let loadingTimer;
let loadingVisible = false;

class ProductGalleryThumbnailControls extends HTMLElement {
  connectedCallback() {
    this.teardown();

    this.abortController = new AbortController();
    this.scroller = this.querySelector('.product-gallery__thumbnail-scroller');
    this.previousButton = this.querySelector('.thumbnail-prev-arrow');
    this.nextButton = this.querySelector('.thumbnail-next-arrow');

    if (!this.scroller || !this.previousButton || !this.nextButton) {
      return;
    }

    const { signal } = this.abortController;
    const updateArrows = () => {
      const maxScroll = this.scroller.scrollWidth - this.scroller.clientWidth;

      this.previousButton.disabled = this.scroller.scrollLeft <= 0;
      this.nextButton.disabled = this.scroller.scrollLeft >= maxScroll - 2;
    };

    this.previousButton.addEventListener('click', () => {
      this.scroller.scrollTo({ left: 0, behavior: 'smooth' });
    }, { signal });

    this.nextButton.addEventListener('click', () => {
      this.scroller.scrollTo({ left: this.scroller.scrollWidth, behavior: 'smooth' });
    }, { signal });

    this.scroller.addEventListener('scroll', updateArrows, { signal });
    this.resizeObserver = new ResizeObserver(updateArrows);
    this.resizeObserver.observe(this.scroller);

    if (document.readyState === 'complete') {
      window.requestAnimationFrame(updateArrows);
    } else {
      window.addEventListener('load', updateArrows, { once: true, signal });
    }

    updateArrows();
  }

  disconnectedCallback() {
    this.teardown();
  }

  teardown() {
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
  }
}

class ProductLineItemController extends HTMLElement {
  connectedCallback() {
    this.teardown();

    const formId = this.getAttribute('form-id');
    const form = formId ? document.getElementById(formId) : null;

    if (!form) {
      return;
    }

    this.abortController = new AbortController();
    this.form = form;

    const { signal } = this.abortController;
    const scope = this.closest('.shopify-section--main-product, .shopify-section--featured-product') || this.parentElement;
    const mainQuantity = scope?.querySelector('.quantity-selector__input[name="quantity"]');
    const bundledItemId = form.querySelector('.items_1_id');
    const bundledItemQuantity = form.querySelector('.items_1_quantity');

    scope?.querySelectorAll('.pillowcase_size_radio').forEach((radio) => {
      radio.addEventListener('change', () => {
        if (bundledItemId) {
          bundledItemId.value = radio.value;
        }

        if (mainQuantity && bundledItemQuantity) {
          bundledItemQuantity.value = mainQuantity.value;
        }
      }, { signal });
    });

    mainQuantity?.addEventListener('change', () => {
      if (bundledItemQuantity) {
        bundledItemQuantity.value = mainQuantity.value;
      }
    }, { signal });

    const lineItemData = form.querySelector(`[data-variant-line-items][data-form-id="${formId}"]`);

    if (!lineItemData) {
      return;
    }

    try {
      this.variantLineItems = JSON.parse(lineItemData.textContent);
    } catch (error) {
      console.error('Error parsing variant line items:', error);
      return;
    }

    const updateLineItemInput = (variantId) => {
      const lineItemValue = this.variantLineItems[variantId];
      const inputId = `line-item-property-${formId}`;
      let lineItemInput = Array.from(form.elements).find((element) => element.id === inputId);

      if (lineItemValue) {
        if (!lineItemInput) {
          lineItemInput = document.createElement('input');
          lineItemInput.type = 'hidden';
          lineItemInput.id = inputId;
          lineItemInput.name = `properties[${this.getAttribute('line-item-name') || ''}]`;
          form.appendChild(lineItemInput);
        }

        lineItemInput.value = lineItemValue;
      } else {
        lineItemInput?.remove();
      }
    };

    const urlVariantId = new URLSearchParams(window.location.search).get('variant');

    if (urlVariantId && Object.hasOwn(this.variantLineItems, urlVariantId)) {
      updateLineItemInput(urlVariantId);
    }

    const variantIdInput = form.querySelector('input[name="id"]');

    if (!variantIdInput) {
      return;
    }

    variantIdInput.addEventListener('change', () => updateLineItemInput(variantIdInput.value), { signal });
    this.variantObserver = new MutationObserver(() => updateLineItemInput(variantIdInput.value));
    this.variantObserver.observe(variantIdInput, { attributes: true, attributeFilter: ['value'] });
  }

  disconnectedCallback() {
    this.teardown();
  }

  teardown() {
    this.abortController?.abort();
    this.variantObserver?.disconnect();
  }
}

class RecentlyViewedProduct extends HTMLElement {
  connectedCallback() {
    const productId = Number(this.getAttribute('product-id'));

    if (!Number.isSafeInteger(productId)) {
      return;
    }

    try {
      const storedProducts = JSON.parse(localStorage.getItem('theme:recently-viewed-products') || '[]');
      const recentlyViewedProducts = storedProducts.filter((item) => item !== productId);

      recentlyViewedProducts.unshift(productId);
      localStorage.setItem('theme:recently-viewed-products', JSON.stringify(recentlyViewedProducts));
    } catch {
      // Safari private browsing can prevent local storage writes.
    }
  }
}

if (!window.customElements.get('product-gallery-thumbnail-controls')) {
  window.customElements.define('product-gallery-thumbnail-controls', ProductGalleryThumbnailControls);
}

if (!window.customElements.get('product-line-item-controller')) {
  window.customElements.define('product-line-item-controller', ProductLineItemController);
}

if (!window.customElements.get('recently-viewed-product')) {
  window.customElements.define('recently-viewed-product', RecentlyViewedProduct);
}

function getLinkedProductLink(target) {
  if (!(target instanceof Element)) {
    return null;
  }

  const link = target.closest(LINK_SELECTOR);

  return link?.closest(MAIN_PRODUCT_SELECTOR) ? link : null;
}

function getSameOriginUrl(link) {
  const url = new URL(link.href, window.location.href);

  return url.origin === window.location.origin ? url : null;
}

function isUnmodifiedPrimaryClick(event, link) {
  return event.button === 0
    && !event.metaKey
    && !event.ctrlKey
    && !event.shiftKey
    && !event.altKey
    && !link.hasAttribute('download')
    && (!link.target || link.target === '_self');
}

function dispatchLoadingEvent(name) {
  document.documentElement.dispatchEvent(new CustomEvent(name, { bubbles: true }));
}

function scheduleLoadingIndicator(navigationId) {
  if (loadingTimer !== undefined) {
    window.clearTimeout(loadingTimer);
  }

  if (loadingVisible) {
    return;
  }

  loadingTimer = window.setTimeout(() => {
    loadingTimer = undefined;

    if (navigationId !== activeNavigationId) {
      return;
    }

    loadingVisible = true;
    dispatchLoadingEvent('theme:loading:start');
  }, LOADING_DELAY_MS);
}

function finishLoadingIndicator(navigationId) {
  if (navigationId !== activeNavigationId) {
    return;
  }

  if (loadingTimer !== undefined) {
    window.clearTimeout(loadingTimer);
    loadingTimer = undefined;
  }

  if (loadingVisible) {
    loadingVisible = false;
    dispatchLoadingEvent('theme:loading:end');
  }
}

function cancelActiveNavigation(section) {
  if (!activeController) {
    return;
  }

  activeController.abort();
  activeController = undefined;
  const cancellationId = ++activeNavigationId;

  section?.removeAttribute('aria-busy');
  finishLoadingIndicator(cancellationId);
}

function preloadLinkedProduct(event) {
  const link = getLinkedProductLink(event.target);

  if (!link || link.getAttribute('aria-current') === 'page') {
    return;
  }

  const url = getSameOriginUrl(link);

  if (!url || prefetchedUrls.has(url.href)) {
    return;
  }

  prefetchedUrls.add(url.href);

  const preloadLink = document.createElement('link');
  preloadLink.rel = 'prefetch';
  preloadLink.as = 'document';
  preloadLink.href = url.href;
  document.head.append(preloadLink);
}

function syncDocumentMetadata(nextDocument, targetUrl) {
  if (nextDocument.title) {
    document.title = nextDocument.title;
  }

  [
    ['link[rel="canonical"]', 'href'],
    ['meta[name="description"]', 'content']
  ].forEach(([selector, attribute]) => {
    const currentElement = document.head.querySelector(selector);
    const nextElement = nextDocument.head.querySelector(selector);

    if (!nextElement) {
      currentElement?.remove();
    } else if (currentElement) {
      currentElement.setAttribute(attribute, nextElement.getAttribute(attribute) || '');
    } else {
      document.head.append(nextElement.cloneNode(true));
    }
  });

  const nextTemplateClass = Array.from(nextDocument.body.classList).find((className) => className.startsWith('template_'));

  Array.from(document.body.classList)
    .filter((className) => className.startsWith('template_'))
    .forEach((className) => document.body.classList.remove(className));

  if (nextTemplateClass) {
    document.body.classList.add(nextTemplateClass);
  }

  window.history.replaceState({ ...(window.history.state || {}), path: targetUrl.href }, '', targetUrl.href);
}

function findSelectedLink(section, group) {
  const selectedLinks = Array.from(section.querySelectorAll(`${LINK_SELECTOR}[aria-current="page"]`));

  return selectedLinks.find((link) => link.dataset.linkedProductGroup === group) || null;
}

function afterTwoAnimationFrames() {
  return new Promise((resolve) => {
    window.requestAnimationFrame(() => window.requestAnimationFrame(resolve));
  });
}

function scrollInstantly(top) {
  window.scrollTo({
    top: Math.max(0, top),
    left: window.scrollX,
    behavior: 'instant'
  });
}

function restoreViewportPosition(element, viewportTop, fallbackScrollTop) {
  if (!element || viewportTop === null) {
    scrollInstantly(fallbackScrollTop);
    return;
  }

  const offset = element.getBoundingClientRect().top - viewportTop;

  if (Math.abs(offset) > 0.5) {
    scrollInstantly(window.scrollY + offset);
  }
}

async function replaceLinkedProduct(link, url, signal, navigationId, shouldRestoreFocus) {
  const currentSection = link.closest(MAIN_PRODUCT_SELECTOR);

  if (!currentSection) {
    throw new Error('Linked product navigation requires a main product section.');
  }

  const focusGroup = link.dataset.linkedProductGroup;
  const currentPicker = link.closest('.variant-picker__option') || link;

  currentSection.setAttribute('aria-busy', 'true');

  const response = await fetch(url.href, {
    credentials: 'same-origin',
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
    signal
  });

  if (!response.ok) {
    throw new Error(`Unable to load linked product (${response.status}).`);
  }

  const responseUrl = new URL(response.url || url.href, window.location.href);
  const historyUrl = responseUrl.origin === window.location.origin ? responseUrl : url;
  const nextDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
  const parsedSection = nextDocument.querySelector(MAIN_PRODUCT_SELECTOR);

  if (!parsedSection) {
    throw new Error('The linked product response did not contain a main product section.');
  }

  // Parse the section in the active document instead of adopting DOMParser nodes.
  // Direct adoption breaks HTMLFormElement named access (form.id no longer resolves
  // to the hidden variant input), which prevents Shopify variant rerenders. Scripts
  // created through innerHTML stay inert, so unrelated inline code is not replayed.
  const sectionContainer = document.createElement('div');
  sectionContainer.innerHTML = parsedSection.outerHTML;
  const nextSection = sectionContainer.querySelector(MAIN_PRODUCT_SELECTOR);

  if (!nextSection) {
    throw new Error('Unable to prepare the linked product section.');
  }

  if (signal.aborted || navigationId !== activeNavigationId) {
    return;
  }

  // Measure immediately before replacement so scrolling during the request is
  // treated as the user's latest position instead of being overwritten.
  const scrollTop = window.scrollY;
  const pickerTop = currentPicker.isConnected ? currentPicker.getBoundingClientRect().top : null;
  const previousMinHeight = nextSection.style.minHeight;
  nextSection.style.minHeight = `${Math.ceil(currentSection.getBoundingClientRect().height)}px`;

  document.dispatchEvent(new CustomEvent('theme:linked-product:before-replace', {
    detail: { productChange: true }
  }));
  currentSection.replaceWith(nextSection);
  syncDocumentMetadata(nextDocument, historyUrl);

  const selectedLink = findSelectedLink(nextSection, focusGroup);
  const nextPicker = selectedLink?.closest('.variant-picker__option') || selectedLink;

  // Correct synchronously so the browser never paints the replacement at a
  // transient scroll position, then correct once more after layout settles.
  restoreViewportPosition(nextPicker, pickerTop, scrollTop);

  try {
    window.Shopify?.PaymentButton?.init?.();
  } catch (error) {
    console.error('Unable to initialize Shopify payment buttons after linked product navigation.', error);
  }

  await afterTwoAnimationFrames();

  if (signal.aborted || navigationId !== activeNavigationId) {
    nextSection.style.minHeight = previousMinHeight;
    return;
  }

  restoreViewportPosition(nextPicker, pickerTop, scrollTop);
  nextSection.style.minHeight = previousMinHeight;
  restoreViewportPosition(nextPicker, pickerTop, scrollTop);

  if (shouldRestoreFocus) {
    selectedLink?.focus({ preventScroll: true });
  }
}

async function navigateToLinkedProduct(event) {
  const link = getLinkedProductLink(event.target);

  if (!link || !isUnmodifiedPrimaryClick(event, link)) {
    return;
  }

  const url = getSameOriginUrl(link);

  if (!url) {
    return;
  }

  event.preventDefault();

  if (link.getAttribute('aria-current') === 'page') {
    cancelActiveNavigation(link.closest(MAIN_PRODUCT_SELECTOR));
    return;
  }

  activeController?.abort();
  const controller = new AbortController();
  activeController = controller;
  const navigationId = ++activeNavigationId;
  // Keyboard-generated clicks use detail 0. Pointer/touch focus would make iOS Safari
  // draw a blue focus ring around the newly selected swatch.
  const shouldRestoreFocus = event.detail === 0;

  scheduleLoadingIndicator(navigationId);

  try {
    await replaceLinkedProduct(link, url, controller.signal, navigationId, shouldRestoreFocus);
  } catch (error) {
    if (error.name === 'AbortError' || controller.signal.aborted || navigationId !== activeNavigationId) {
      return;
    }

    console.error('Linked product navigation failed; falling back to a full navigation.', error);
    window.location.assign(url.href);
  } finally {
    if (navigationId === activeNavigationId) {
      activeController = undefined;
    }

    finishLoadingIndicator(navigationId);
  }
}

document.addEventListener('click', navigateToLinkedProduct);
document.addEventListener('pointerover', preloadLinkedProduct, { passive: true });
document.addEventListener('focusin', preloadLinkedProduct);
document.addEventListener('touchstart', preloadLinkedProduct, { passive: true });
